import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import React from "react";
import { m } from "framer-motion";
import { hasItems } from "~/features/app/utils";
import { useAutoPlay } from "~/features/blocks/inventory/deep-dive-gallery/use-autoplay";
import { DeepDiveGallerySpinner } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-spinner";
import { DeepDiveGalleryControlDirection } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-controls/deep-dive-gallery-control-direction";
import { useDeepDiveGallery, type DeepDiveGalleryControl as DeepDiveGalleryControlData } from "~/features/blocks/inventory/deep-dive-gallery/context";
import { Icon } from "~/features/ui/icon";
import { useScrollToActiveElement } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-controls/use-scroll-to";
import ArrowLeft from "~/public/icons/arrow-left.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";
export type DeepDiveGalleryControlsViewProps<TControl = DeepDiveGalleryControlData> = {
  id: string;
  controls: TControl[];
  getControlKey?: (control: TControl, index: number) => React.Key;
  renderControl: (control: TControl, index: number) => React.ReactNode;
};
export function DeepDiveGalleryControlsView<TControl = DeepDiveGalleryControlData>(props: DeepDiveGalleryControlsViewProps<TControl>) {
  const {
    id,
    controls,
    getControlKey,
    renderControl
  } = props;
  const targetId = `deep-dive-gallery-controls-${id}`;
  useAutoPlay({
    controls: controls as DeepDiveGalleryControlData[],
    targetId
  });
  const {
    videoPreloadStatus,
    activeStepIndex,
    isVideoPlaying,
    goPrev,
    goNext,
    setRenderGalleryCursor
  } = useDeepDiveGallery();
  const controlsRef = React.useRef<HTMLDivElement>(null);
  useScrollToActiveElement(controlsRef, activeStepIndex, "control");
  const opacity = videoPreloadStatus === "PRELOADING" ? 0 : 1;
  const isPrevDisabled = isVideoPlaying || activeStepIndex === 0;
  const isNextDisabled = isVideoPlaying || activeStepIndex === controls.length - 1;
  if (!hasItems(controls)) {
    return null;
  }
  return <_StyledDiv id={targetId} onMouseEnter={() => setRenderGalleryCursor(false)} onMouseLeave={() => setRenderGalleryCursor(true)} role="presentation">
      <_StyledDiv2>
        <DeepDiveGallerySpinner show={videoPreloadStatus === "PRELOADING"} />

        <_StyledMDiv ref={controlsRef} initial={{
        opacity: 0
      }} animate={{
        opacity
      }}>
          <_StyledDeepDiveGalleryControlDirection aria-label="Explore previous" disabled={isPrevDisabled} style={{
          pointerEvents: isPrevDisabled ? "none" : "auto"
        }} onClick={() => goPrev()}>
            <Icon icon={ArrowLeft} />
          </_StyledDeepDiveGalleryControlDirection>

          <_StyledDiv3>
            {controls.map((control, idx) => <React.Fragment key={getControlKey?.(control, idx) ?? idx}>{renderControl(control, idx)}</React.Fragment>)}
          </_StyledDiv3>

          <_StyledDeepDiveGalleryControlDirection2 aria-label="Explore next" disabled={isNextDisabled} style={{
          pointerEvents: isNextDisabled ? "none" : "auto"
        }} onClick={() => goNext()}>
            <Icon icon={ArrowRight} />
          </_StyledDeepDiveGalleryControlDirection2>
        </_StyledMDiv>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-kb61tp-0"
})({
  "position": "absolute",
  "left": "var(--spacing-lg)",
  "right": "var(--spacing-lg)",
  "bottom": "var(--spacing-md)",
  "zIndex": "20",
  "display": "flex",
  "userSelect": "none",
  "justifyContent": "center",
  "color": "var(--white-static)",
  "@media (min-width: 1024px)": {
    "bottom": "4rem",
    "left": "50%",
    "right": "auto",
    "--tw-translate-x": "-50%",
    "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
  }
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-kb61tp-1"
})({
  "maxWidth": "100%",
  "borderRadius": "4px",
  "backgroundColor": "rgba(255, 255, 255, 0.1)",
  "padding": "0.4rem",
  "--tw-backdrop-blur": "blur(8px)",
  "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
});
var _StyledMDiv = _styled3(m.div).withConfig({
  componentId: "sc-sf-kb61tp-2"
})({
  "display": "flex",
  "alignItems": "center",
  "columnGap": "0.4rem"
});
var _StyledDeepDiveGalleryControlDirection = _styled4(DeepDiveGalleryControlDirection).withConfig({
  componentId: "sc-sf-kb61tp-3"
})({
  "display": "inline-flex"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-kb61tp-4"
})({
  "display": "flex",
  "alignItems": "center",
  "columnGap": "0.4rem",
  "overflowX": "auto",
  "overflowY": "hidden"
});
var _StyledDeepDiveGalleryControlDirection2 = /*#__PURE__*/_styled6(DeepDiveGalleryControlDirection).withConfig({
  displayName: "deep-dive-gallery-controls___StyledDeepDiveGalleryControlDirection2",
  componentId: "sc-l3hyzj-0"
})({
  "display": "inline-flex"
});